import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShieldCheck, AlertTriangle, FileText, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { NotFound } from "./Frameworks";

export function SingaporeHub() {
  const [regs, setRegs] = useState([]);
  useEffect(() => { api.get("/regulators").then(r => setRegs(r.data.regulators)); }, []);

  return (
    <div data-testid="page-singapore-hub">
      <section className="border-b border-slate-200 relative overflow-hidden">
        <div className="bg-grid absolute inset-0 opacity-50" />
        <div className="container-rp py-16 lg:py-24 relative">
          <div className="overline flex items-center gap-2"><span>🇸🇬</span> Singapore Regulatory Hub</div>
          <h1 className="font-display text-5xl lg:text-7xl font-bold mt-4 tracking-tight">
            Eight regulators.<br />
            <span style={{ color: "var(--rp-accent)" }}>One operating manual.</span>
          </h1>
          <p className="text-slate-600 mt-6 max-w-2xl text-lg">
            Singapore's most complete BFSI compliance deep-dive — MAS, PDPC, CSA and the rest. Control catalogues, applicability matrices, compliance calendars and penalty frameworks.
          </p>
          <div className="mt-8 flex flex-wrap gap-6 text-sm">
            <Stat n="8" l="regulators" />
            <Stat n="9" l="PDPA obligations" />
            <Stat n="12" l="templates" />
            <Stat n="72h" l="PDPA breach clock" />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-rp">
          <div className="overline">Regulator deep-dives</div>
          <h2 className="font-display text-4xl font-semibold mt-3">Every Singapore rulebook for BFSI.</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 mt-10">
            {regs.map(r => (
              <Link to={`/singapore/${r.slug}`} key={r.slug} className="bg-white p-6 hover:bg-slate-50 transition-colors block" data-testid={`reg-card-${r.slug}`}>
                <div className={`h-1 w-12 bg-gradient-to-r ${r.color} rounded-sm`} />
                <div className="font-display text-xl font-semibold mt-4">{r.name}</div>
                <div className="text-xs text-slate-500 mt-1">{r.authority}</div>
                <p className="text-sm text-slate-600 mt-4 leading-relaxed line-clamp-3">{r.summary}</p>
                <div className="mt-5 flex items-center justify-between text-xs">
                  <span className="font-mono text-slate-500">{r.year}</span>
                  <span style={{ color: "var(--rp-accent)" }} className="font-medium inline-flex items-center gap-1">Open <ArrowRight size={12} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Quick playbooks */}
      <section className="section bg-slate-50 border-y border-slate-200">
        <div className="container-rp">
          <div className="overline">Cross-cutting playbooks</div>
          <h2 className="font-display text-4xl font-semibold mt-3">When regulators overlap.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {[
              { icon: AlertTriangle, title: "Multi-regulator breach playbook", desc: "PDPC · MAS · CSA — escalation order, who to call first, evidence pack." , to: "/singapore/breach-playbook" },
              { icon: FileText, title: "Templates library", desc: "RoPA · IR plan · BCP · outsourcing register · DPIA · 12 templates total.", to: "/singapore/templates" },
              { icon: ShieldCheck, title: "Regulator overlap matrix", desc: "Where MAS TRM, PDPA, CCoP and MAS Notice 655 align, conflict and layer.", to: "/singapore/overlap" },
            ].map(p => (
              <Link to={p.to} key={p.title} className="rp-card p-6">
                <p.icon size={22} style={{ color: "var(--rp-accent)" }} />
                <div className="font-display text-xl font-semibold mt-4">{p.title}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{p.desc}</p>
                <div className="text-xs font-medium mt-5 inline-flex items-center gap-1" style={{ color: "var(--rp-accent)" }}>Open <ChevronRight size={12} /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display text-3xl font-bold" style={{ color: "var(--rp-primary)" }}>{n}</div>
      <div className="text-xs text-slate-500 uppercase tracking-widest font-mono">{l}</div>
    </div>
  );
}

export function RegulatorDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/regulators/${slug}`).then(res => setR(res.data)).catch(() => setErr(true)); }, [slug]);

  if (err) return <NotFound title="Regulator not found" />;
  if (!r) return <div className="container-rp py-24 text-slate-400">Loading…</div>;

  return (
    <div data-testid={`page-reg-${r.slug}`}>
      <section className="border-b border-slate-200">
        <div className={`h-1.5 bg-gradient-to-r ${r.color}`} />
        <div className="container-rp py-12">
          <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-6"><ArrowLeft size={12} /> Back to Singapore Hub</button>
          <div className="overline">{r.authority} · {r.year}</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">{r.name}</h1>
          <p className="text-slate-600 mt-4 text-lg max-w-3xl">{r.summary}</p>
        </div>
      </section>

      <section className="section">
        <div className="container-rp grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="overline">Scope</div>
            <h2 className="font-display text-3xl font-semibold mt-3">Who must comply</h2>
            <p className="text-slate-600 mt-3 leading-relaxed">{r.scope}</p>

            <div className="mt-12">
              <div className="overline">Key controls & obligations</div>
              <h2 className="font-display text-3xl font-semibold mt-3">The control catalogue</h2>
              <ul className="mt-6 space-y-3">
                {r.key_controls.map((c, i) => (
                  <li key={i} className="rp-card p-4 flex gap-4">
                    <div className="font-mono text-xs text-slate-400 pt-0.5">{String(i + 1).padStart(2, "0")}</div>
                    <div className="text-sm text-slate-700 leading-relaxed">{c}</div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-12 rp-card p-6 bg-slate-50">
              <div className="overline">Educational disclaimer</div>
              <p className="text-sm text-slate-600 mt-2">RiskPedia content is for educational purposes only. Not legal or regulatory advice. Refer to the {r.authority} for binding text.</p>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="rp-card p-6 sticky top-24">
              <div className="overline">Take action</div>
              <ul className="mt-4 space-y-3 text-sm">
                <li><Link to="/tools/csrs-calculator" className="flex items-center justify-between p-3 border border-slate-200 hover:border-slate-400 rounded-sm">Score with CSRS <ArrowRight size={14} /></Link></li>
                <li><Link to="/tools/pdpa-scorer" className="flex items-center justify-between p-3 border border-slate-200 hover:border-slate-400 rounded-sm">PDPA Readiness <ArrowRight size={14} /></Link></li>
                <li><Link to="/tools/mas-trm-readiness" className="flex items-center justify-between p-3 border border-slate-200 hover:border-slate-400 rounded-sm">MAS TRM Heat-map <ArrowRight size={14} /></Link></li>
                <li><Link to="/glossary" className="flex items-center justify-between p-3 border border-slate-200 hover:border-slate-400 rounded-sm">Glossary <ArrowRight size={14} /></Link></li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
